import { $, $$, el, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';

interface WarnAction {
  threshold: number;
  action: string;
  durationMs: number;
}

interface ModerationData {
  settings: { warnActions: WarnAction[]; dmPolicy: Record<string, string> };
  sanctions: string[];
  guildId: string;
}

/**
 * Moderation settings.
 *
 * The escalation ladder lives outside the form and is rebuilt from the DOM, in
 * the same way the reward tiers of the Levels module are: a variable number of
 * rows cannot be expressed by fixed field names.
 */
export function initModerationPage(): void {
  const found = $<HTMLFormElement>('[data-moderation-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  // Armed first: saving must not depend on any widget mounting.
  const rows = $('[data-warn-rows]', form);
  const empty = $('[data-warn-empty]', form);

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/moderation?guildId=${guildId}`, {
        guildId,
        reasonPresets: lines(form, 'reasonPresets'),
        hardmute: checkbox(form, 'hardmute'),
        deleteCommands: checkbox(form, 'deleteCommands'),
        replyWithReason: checkbox(form, 'replyWithReason'),
        dmBeforeBan: checkbox(form, 'dmBeforeBan'),
        dmPolicy: Object.fromEntries(
          (data?.sanctions ?? []).map((action) => [
            action,
            radio(form, `dmPolicy.${action}`, 'notify'),
          ]),
        ),
        moderatorRoleIds: findSelectMenu(form, 'moderatorRoleIds')?.value ?? [],
        muteRoleId: findSelectMenu(form, 'muteRoleId')?.single ?? '',
        ignoredRoleIds: findSelectMenu(form, 'ignoredRoleIds')?.value ?? [],
        ignoredChannelIds: findSelectMenu(form, 'ignoredChannelIds')?.value ?? [],
        imageOnlyChannelIds: findSelectMenu(form, 'imageOnlyChannelIds')?.value ?? [],
        warnActions: readTiers(),
        warnExpiryDays: number(form, 'warnExpiryDays', 0),
      });
    },
  });

  const data = pageData<ModerationData>();

  /** Rebuilds the ladder from the rows on screen. */
  function readTiers(): WarnAction[] {
    return $$<HTMLElement>('[data-warn-row]', form)
      .map((row) => ({
        threshold: Number(row.querySelector<HTMLInputElement>('[data-warn-threshold]')?.value ?? 0),
        action: row.querySelector<HTMLSelectElement>('[data-warn-action]')?.value ?? 'mute',
        durationMs: minutes(
          row.querySelector<HTMLInputElement>('[data-warn-duration]')?.value ?? '',
        ),
      }))
      .filter((tier) => tier.threshold > 0);
  }

  function renderEmpty(): void {
    empty?.classList.toggle('hidden', $$('[data-warn-row]', form).length > 0);
  }

  /** One rung: a count, a sanction, an optional duration. */
  function addRow(tier: WarnAction = { threshold: 3, action: 'mute', durationMs: 0 }): void {
    if (!rows) return;

    const row = el('div', {
      class: 'flex flex-wrap items-end gap-2',
      attrs: { 'data-warn-row': '' },
    });

    const count = el('input', {
      class: 'input input-bordered w-24',
      attrs: {
        type: 'number',
        min: '1',
        max: '100',
        'data-warn-threshold': '',
        'aria-label': ct('warnThreshold', 'Warnings'),
      },
    });
    count.value = String(tier.threshold);

    const action = el('select', {
      class: 'select select-bordered min-w-40',
      attrs: { 'data-warn-action': '', 'aria-label': ct('warnAction', 'Action') },
    });
    for (const name of data?.sanctions ?? []) {
      action.append(el('option', { text: ct(`modAction_${name}`, name), attrs: { value: name } }));
    }
    action.value = tier.action;

    const duration = el('input', {
      class: 'input input-bordered w-32',
      attrs: {
        type: 'text',
        placeholder: '1d',
        'data-warn-duration': '',
        'aria-label': ct('warnDuration', 'Duration'),
      },
    });
    duration.value = tier.durationMs > 0 ? `${String(Math.round(tier.durationMs / 60000))}m` : '';

    const remove = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: '✕',
      attrs: {
        type: 'button',
        'aria-label': ct('removeItem', 'Remove {name}', { name: '' }).trim(),
      },
    });
    remove.addEventListener('click', () => {
      row.remove();
      renderEmpty();
      form.dispatchEvent(new Event('change', { bubbles: true }));
    });

    row.append(count, action, duration, remove);
    rows.append(row);
    renderEmpty();
  }

  for (const tier of data?.settings.warnActions ?? []) addRow(tier);
  renderEmpty();

  $('[data-warn-add]', form)?.addEventListener('click', () => {
    addRow();
    form.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**
 * Reads `2h30m` into milliseconds.
 *
 * Duplicated from the server's parser rather than shared: the client bundle
 * must not import from `services/`, and the alternative - sending the raw text
 * and parsing it once on the server - would leave the dashboard unable to show
 * back what it saved.
 */
function minutes(input: string): number {
  const units: Record<string, number> = {
    s: 1000,
    m: 60_000,
    h: 3_600_000,
    d: 86_400_000,
    w: 604_800_000,
  };
  let total = 0;

  for (const match of input.toLowerCase().matchAll(/(\d+)\s*([smhdw])/g)) {
    total += Number(match[1]) * (units[match[2] ?? ''] ?? 0);
  }

  if (total === 0 && /^\d+$/.test(input.trim())) total = Number(input.trim()) * 60_000;
  return total;
}

function radio(form: HTMLFormElement, name: string, fallback: string): string {
  const field = form.elements.namedItem(name);
  if (field instanceof RadioNodeList) return field.value || fallback;
  return field instanceof HTMLInputElement ? field.value || fallback : fallback;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}

function number(form: HTMLFormElement, name: string, fallback: number): number {
  const field = form.elements.namedItem(name);
  const value = field instanceof HTMLInputElement ? Number(field.value) : fallback;
  return Number.isFinite(value) ? value : fallback;
}

/** A textarea read as one entry per line, blank lines dropped. */
function lines(form: HTMLFormElement, name: string): string[] {
  const field = form.elements.namedItem(name);
  if (!(field instanceof HTMLTextAreaElement)) return [];

  return field.value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}
